import createLogger from '@/utils/logger'
import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import type { NavigationGuardNext, RouteLocationNormalizedGeneric } from 'vue-router'
import { usePaymentMethodStore } from '@/stores/paymentMethod'
import { hardReloadOnStoreError } from '@/utils/storeUtils'

const logger = createLogger('router/guards/waitForStores.ts')

export async function waitForStores(
  _to: RouteLocationNormalizedGeneric,
  _from: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext,
) {
  const authStore = useAuthStore()

  // If auth is still loading, wait for it
  try {
    const authloadingCompleted = await authStore.untilLoadingIsDone()
    if (!authloadingCompleted) {
      logger.error('Error waiting for auth state, hard reloading')
      await hardReloadOnStoreError()
      return
    }
  } catch (_error) {
    logger.error('Error waiting for auth state, signing out and redirecting to login')
    await authStore.signOut({
      redirectToLogin: true,
      errorMessage: 'An error occurred while loading the app. Please try to login again.',
      hardReloadToLogin: true,
    })
    return
  }

  if (authStore.isCustomer) {
    const customerStore = useCustomerStore()

    try {
      const loadingCompleted = await customerStore.untilLoadingIsDone()
      if (!loadingCompleted) {
        logger.error('Error waiting for customer state, hard reloading')
        await hardReloadOnStoreError()
        return
      }
    } catch (_error) {
      logger.error('Error waiting for customer state, signing out and redirecting to login')
      await authStore.signOut({
        redirectToLogin: true,
        errorMessage: 'An error occurred while loading the app. Please try to login again.',
        hardReloadToLogin: true,
      })
      return
    }
  }

  if (authStore.isCustomer) {
    const paymentMethodStore = usePaymentMethodStore()

    try {
      const loadingCompleted = await paymentMethodStore.untilLoadingIsDone()
      if (!loadingCompleted) {
        logger.error('Error waiting for payment method state, hard reloading')
        await hardReloadOnStoreError()
        return
      }
    } catch (_error) {
      logger.error('Error waiting for payment method state, signing out and redirecting to login')
      await authStore.signOut({
        redirectToLogin: true,
        errorMessage: 'An error occurred while loading the app. Please try to login again.',
        hardReloadToLogin: true,
      })
      return
    }
  }

  next()
}
